import React, { PropsWithChildren } from 'react';
import { SerializedStyles } from '@emotion/react';
import { Container } from '@mui/material';
import ContentLoader from 'react-content-loader';
import Slider from 'react-slick';

import { Header, Section, Wrapper } from './BaseCarousel/elements';

interface Props {
  cardDimension?: { width: number; height: number };
  customSvg?: React.ReactNode;
  buttonDimensions?: { buttonHeight: number; buttonWidth: number };
  noButton?: boolean;
  headerDimensions?: { headerHeight: number; headerWidth: number };
  containerHeight?: string;
  rows?: number;
  cardCount?: number;
  hoverLabel?: string;
  heading?: string;
  headerCSS?: SerializedStyles | {};
}

export const CarouselPlaceholder: React.FC<PropsWithChildren<Props>> = ({
  cardDimension = { width: 282, height: 320 },
  customSvg,
  buttonDimensions = { buttonHeight: 15, buttonWidth: 70 },
  headerDimensions = { headerHeight: 15, headerWidth: 75 },
  rows = 1,
  cardCount = 8,
  containerHeight = '456px',
  hoverLabel = '',
  noButton,
  headerCSS,
  heading,
}) => {
  const { width, height } = cardDimension;
  const { buttonHeight, buttonWidth } = buttonDimensions;
  const { headerHeight, headerWidth } = headerDimensions;
  const placeholderRows = rows;

  const placeholderSlickSettings = {
    dots: false,
    infinite: true,
    speed: 500,
    rows: placeholderRows,
    slidesToShow: 5,
    slidesToScroll: 1,
    arrows: false,
    variableWidth: true,
    centerPadding: '0',
  };

  const renderHeaderPlaceholder = (Height: number, Width: number) => {
    return (
      <span style={{ height: 14, width: 91 }}>
        <ContentLoader
          style={{ height: Height, width: Width }}
          foregroundColor="#f3f3f3"
          backgroundColor="var(--Grey-Accent-Color)"
          height={Height}
          width={Width}
          speed={3}
          title={hoverLabel}
          uniqueKey={`h.h${Height}.w${Width}`}
        >
          <rect x={0} y={0} rx={10} ry={10} height={Height} width={Width} />
        </ContentLoader>
      </span>
    );
  };

  const renderCardPlaceholders = (Width: number, Height: number) => {
    const cardPlaceholders = [];
    for (let i = 0; i < cardCount; i++) {
      cardPlaceholders.push(
        <div key={i}>
          <ContentLoader
            style={{ height: Height, width: Width }}
            foregroundColor="#f3f3f3"
            backgroundColor="var(--Grey-Accent-Color)"
            height={Height}
            width={Width}
            speed={3}
            title={hoverLabel}
            uniqueKey={`c.h${Height}.w${Width}`}
          >
            {customSvg ? (
              customSvg
            ) : (
              <rect x={0} y={0} rx={10} ry={10} height={Height} width={Width} />
            )}
          </ContentLoader>
        </div>,
      );
    }
    return cardPlaceholders;
  };

  const renderButtonPlaceholder = () => {
    if (noButton) {
      return <> </>;
    }

    return (
      <ContentLoader
        style={{ height: buttonHeight, width: buttonWidth }}
        foregroundColor="#f3f3f3"
        backgroundColor="var(--Grey-Accent-Color)"
        height={buttonHeight}
        width={buttonWidth}
        speed={3}
        title={hoverLabel}
        uniqueKey={`b.h${buttonHeight}.w${buttonWidth}`}
      >
        <rect x={0} y={0} rx={10} ry={10} height={buttonHeight} width={buttonWidth} />
      </ContentLoader>
    );
  };

  return (
    <div
      style={{
        height: containerHeight,
        boxSizing: 'content-box',
        textAlign: 'center',
        overflow: 'hidden',
      }}
    >
      <Section>
        <Container maxWidth="xl">
          <Header css={headerCSS}>
            {heading ? <span>{heading}</span> : renderHeaderPlaceholder(headerHeight, headerWidth)}
            {renderButtonPlaceholder()}
          </Header>
          <Wrapper data-test-percy-hidden lineWidthPercentage={0}>
            <Slider {...placeholderSlickSettings}>{renderCardPlaceholders(width, height)}</Slider>
          </Wrapper>
        </Container>
      </Section>
    </div>
  );
};
